import React, { useState } from 'react';
import {
    Box,
    TextField,
    Typography,
    Button,
    Paper,
    IconButton,
    Chip,
    Alert,
    Divider
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    SwapHoriz as MatchIcon,
    AutoAwesome as AiIcon
} from '@mui/icons-material';
import RichTextInput from './RichTextInput';
import QuizGenerator from '../QuizGenerator';

interface MatchingEditorProps {
    content: any;
    onChange: (content: any) => void;
}

interface MatchPair {
    id: string;
    left: string;
    right: string;
}

const MatchingEditor: React.FC<MatchingEditorProps> = ({ content, onChange }) => {
    const [generatorOpen, setGeneratorOpen] = useState(false);
    const instruction = content?.instruction || '';
    const learningContent = content?.learningContent || '';
    const pairs: MatchPair[] = content?.pairs || [];
    const shuffleRight = content?.shuffleRight !== false; // Default true

    const addPair = () => {
        const newPair: MatchPair = {
            id: Date.now().toString(),
            left: '',
            right: ''
        };
        onChange({ ...content, pairs: [...pairs, newPair] });
    };

    const handleQuestionsGenerated = (generatedQuestions: any[]) => {
        const generatedPairs: MatchPair[] = generatedQuestions
            .filter(gq => gq.left && gq.right)
            .map((gq, index) => ({
                id: `${Date.now()}-${index}`,
                left: String(gq.left),
                right: String(gq.right)
            }));

        if (generatedPairs.length === 0) {
            return;
        }

        onChange({ ...content, pairs: [...pairs, ...generatedPairs] });
    };

    const updatePair = (pairId: string, field: string, value: string) => {
        const updatedPairs = pairs.map(pair =>
            pair.id === pairId ? { ...pair, [field]: value } : pair
        );
        onChange({ ...content, pairs: updatedPairs });
    };

    const deletePair = (pairId: string) => {
        const updatedPairs = pairs.filter(pair => pair.id !== pairId);
        onChange({ ...content, pairs: updatedPairs });
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Zuordnungsaufgabe (Matching)</Typography>
                <Button
                    variant="outlined"
                    startIcon={<AiIcon />}
                    onClick={() => setGeneratorOpen(true)}
                >
                    KI-Generator
                </Button>
            </Box>

            <Alert severity="info">
                Lernende verbinden Elemente aus der linken Liste mit den passenden Elementen aus der rechten Liste.
            </Alert>

            {/* Learning content (optional) */}
            <RichTextInput
                label="Lerninhalt (optional)"
                value={learningContent}
                onChange={(val) => onChange({ ...content, learningContent: val })}
                helperText="Dieser formatierte Text wird im Player über dem Quiz angezeigt."
                placeholder="Optionaler Lernstoff, der über dem Quiz angezeigt wird..."
            />

            {/* Instruction */}
            <TextField
                label="Aufgabenstellung"
                fullWidth
                multiline
                rows={3}
                value={instruction}
                onChange={(e) => onChange({ ...content, instruction: e.target.value })}
                placeholder="Ordnen Sie die Begriffe den passenden Definitionen zu..."
            />

            {/* Pairs */}
            <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1">Zuordnungs-Paare</Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addPair}>
                        Paar hinzufügen
                    </Button>
                </Box>

                {pairs.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center">
                        Noch keine Paare definiert
                    </Typography>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {pairs.map((pair, index) => (
                            <Paper key={pair.id} sx={{ p: 2, bgcolor: 'background.default' }}>
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Chip label={`Paar ${index + 1}`} color="primary" size="small" />

                                    {/* Left Item */}
                                    <TextField
                                        label="Linke Seite"
                                        size="small"
                                        fullWidth
                                        required
                                        value={pair.left}
                                        onChange={(e) => updatePair(pair.id, 'left', e.target.value)}
                                        placeholder="z.B. Begriff"
                                        sx={{ flex: 1 }}
                                    />

                                    <MatchIcon color="action" />

                                    {/* Right Item */}
                                    <TextField
                                        label="Rechte Seite"
                                        size="small"
                                        fullWidth
                                        required
                                        value={pair.right}
                                        onChange={(e) => updatePair(pair.id, 'right', e.target.value)}
                                        placeholder="z.B. Definition"
                                        sx={{ flex: 1 }}
                                    />

                                    <IconButton
                                        onClick={() => deletePair(pair.id)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Settings */}
            <Paper sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    Einstellungen
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                        type="checkbox"
                        checked={shuffleRight}
                        onChange={(e) => onChange({ ...content, shuffleRight: e.target.checked })}
                        id="shuffle-right"
                    />
                    <label htmlFor="shuffle-right">
                        <Typography variant="body2">
                            Rechte Seite zufällig mischen (empfohlen)
                        </Typography>
                    </label>
                </Box>
            </Paper>

            {/* Summary */}
            {pairs.length > 0 && (
                <Alert severity="success">
                    {pairs.length} Zuordnungs-Paar(e) definiert
                </Alert>
            )}

            <QuizGenerator
                open={generatorOpen}
                onClose={() => setGeneratorOpen(false)}
                onQuestionsGenerated={handleQuestionsGenerated}
                defaultQuestionType="matching"
            />
        </Box>
    );
};

export default MatchingEditor;
